import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { OktaService } from './okta.service';
import { environment } from '../../../environments/environment';
import { HcToastOptions, HcToasterService } from '@healthcatalyst/cashmere';

export interface SLAElement {
    'slaid': number,
    'name': string,
    'description': string
}


@Injectable()
export class SLAService {
    slaList: BehaviorSubject<SLAElement[] | null> = new BehaviorSubject<SLAElement[] | null>(null);
    slaKey: BehaviorSubject<{ [key: string]: string } | null> = new BehaviorSubject<{ [key: string]: string } | null>(null);
    loadFailed = false;

    constructor(private http: HttpClient, private oktaService: OktaService, private toasterService: HcToasterService) { };

    get slaListUpdated(): Observable<SLAElement[] | null> {
        return this.slaList.asObservable();
    }

    get slaKeyUpdated(): Observable<{ [key: string]: string } | null> {
        return this.slaKey.asObservable();
    }

    createKey(productLineList: SLAElement[]) {
        let keyObj: { [key: string]: string } = {};

        productLineList.forEach(element => {
            keyObj[element.slaid.toString()] = element.name;
        });

        return keyObj
    }

    refresh() {
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = { headers: headers };

        this.http.get<any>(environment.azure.url + '/slas', options)
            .subscribe({
                next: data => {
                    this.slaList.next(data);
                    this.slaKey.next(this.createKey(data));
                    this.loadFailed = false;
                },
                error: msg => {
                    this.loadFailed = true;
                    this.slaList.next(null);
                    console.log(msg);
                }
            });
    }

    add(target: SLAElement) {
        // Write target to database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = { headers: headers };

        const postSub = this.http.post<any>(environment.azure.url + '/admin.slas.add',
            {
                name: target.name,
                description: target.description
            },
            options
        ).subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'SLA Added',
                    body: target.name + " successfully added.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Add Failed',
                    body: "Unable to add SLA. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log(msg);
                postSub.unsubscribe();
            }
        });
    }

    update(target: SLAElement) {
        // Update target in databases
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = { headers: headers };

        const postSub = this.http.post<any>(environment.azure.url + '/admin.slas.update',
            {
                slaid: target.slaid,
                name: target.name,
                description: target.description
            },
            options
        ).subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'SLA Updated',
                    body: target.name + " successfully updated.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Update Failed',
                    body: "Unable to update sla. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log(msg);
                postSub.unsubscribe();
            }
        });
    }

    remove(target: SLAElement) {
        //Remove target from database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = { headers: headers };

        const postSub = this.http.post<any>(environment.azure.url + '/admin.slas.remove',
            {
                slaid: target.slaid
            },
            options
        ).subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'SLA Removed',
                    body: target.name + " successfully removed.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Remove Failed',
                    body: "Unable to remove sla. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log(msg);
                postSub.unsubscribe();
            }
        });
    }
}